import { useId } from 'react'
import { PERIODOS_META_ADS, type ContaAnuncioResumo, type PeriodoMetaAds } from '@baiocchi/shared'
import { Botao, Entrada, Selecao } from '@baiocchi/ui'

import type { Filtros } from '../../ganchos/usar-filtros-url'

/**
 * Seletor de conta e período — o topo de todas as telas de `/meta-ads`.
 *
 * ## Os filtros vivem na URL
 *
 * Mesma decisão de `useFiltrosUrl` no resto do CRM, e aqui rende três coisas concretas: o
 * gestor manda ao sócio o link do painel já na conta de aluguel e nos últimos 30 dias; o
 * botão "voltar" desfaz o filtro e não a navegação; e trocar de aba (visão geral →
 * campanhas) **preserva** o recorte, em vez de reabrir no padrão.
 *
 * ## "Todas as contas" é a opção padrão, e é uma decisão
 *
 * O §1 da missão pede a visão consolidada, e ela é o que o dono da imobiliária quer ver ao
 * abrir a tela: quanto a empresa investiu e quanto rendeu. O recorte por conta é o passo
 * seguinte, não o primeiro.
 *
 * Consolidado inclui só contas **ativas** — conta inativa existe para o histórico, e somá-la
 * faria o CPL do período incluir uma operação encerrada.
 */

const ROTULO_PERIODO: Readonly<Record<PeriodoMetaAds, string>> = {
  HOJE: 'Hoje',
  ONTEM: 'Ontem',
  '7D': 'Últimos 7 dias',
  '14D': 'Últimos 14 dias',
  '30D': 'Últimos 30 dias',
  PERSONALIZADO: 'Personalizado',
}

export interface PropriedadesFiltrosMetaAds {
  readonly filtros: Filtros
  readonly definir: (novos: Record<string, string | number | boolean | null | undefined>) => void
  readonly contas: ReadonlyArray<ContaAnuncioResumo>
  readonly carregandoContas: boolean
}

export function FiltrosMetaAds({
  filtros,
  definir,
  contas,
  carregandoContas,
}: PropriedadesFiltrosMetaAds) {
  const periodo = (filtros.periodo ?? '7D') as PeriodoMetaAds
  const ativas = contas.filter((conta) => conta.ativo)

  // `useId` e não um id fixo: esta barra aparece em sete telas, e um id repetido faria o
  // rótulo de uma apontar para o controle de outra se as duas convivessem na página.
  const base = useId()
  const idConta = `${base}-conta`
  const idPeriodo = `${base}-periodo`
  const idDe = `${base}-de`
  const idAte = `${base}-ate`

  return (
    <div className="mb-4 flex flex-wrap items-end gap-3">
      <label htmlFor={idConta} className="flex flex-col gap-1">
        <span className="text-xs font-medium uppercase tracking-wide text-conteudo-suave">
          Conta
        </span>
        <Selecao
          id={idConta}
          value={filtros.contaId ?? ''}
          onChange={(evento) => definir({ contaId: evento.target.value })}
          disabled={carregandoContas}
          aria-label="Conta de anúncio"
          opcoes={[
            { valor: '', rotulo: 'Todas as contas' },
            ...ativas.map((conta) => ({
              valor: String(conta.id),
              rotulo: `${conta.nome} · ${rotularOperacao(conta.operacao)}`,
            })),
          ]}
        />
      </label>

      <label htmlFor={idPeriodo} className="flex flex-col gap-1">
        <span className="text-xs font-medium uppercase tracking-wide text-conteudo-suave">
          Período
        </span>
        <Selecao
          id={idPeriodo}
          value={periodo}
          onChange={(evento) => {
            const proximo = evento.target.value as PeriodoMetaAds
            // Sair de `PERSONALIZADO` limpa as datas: mandá-las junto de `7D` faria o
            // servidor recusar com 422 em vez de ignorá-las.
            definir(
              proximo === 'PERSONALIZADO'
                ? { periodo: proximo }
                : { periodo: proximo, de: null, ate: null },
            )
          }}
          aria-label="Período"
          opcoes={PERIODOS_META_ADS.map((valor) => ({
            valor,
            rotulo: ROTULO_PERIODO[valor],
          }))}
        />
      </label>

      {periodo === 'PERSONALIZADO' ? (
        <>
          <label htmlFor={idDe} className="flex flex-col gap-1">
            <span className="text-xs font-medium uppercase tracking-wide text-conteudo-suave">
              De
            </span>
            <Entrada
              id={idDe}
              type="date"
              value={filtros.de ?? ''}
              onChange={(evento) => definir({ de: evento.target.value })}
              aria-label="Data inicial"
            />
          </label>
          <label htmlFor={idAte} className="flex flex-col gap-1">
            <span className="text-xs font-medium uppercase tracking-wide text-conteudo-suave">
              Até
            </span>
            <Entrada
              id={idAte}
              type="date"
              value={filtros.ate ?? ''}
              onChange={(evento) => definir({ ate: evento.target.value })}
              aria-label="Data final"
            />
          </label>
        </>
      ) : null}

      {temFiltroAtivo(filtros) ? (
        <Botao
          variante="fantasma"
          tamanho="sm"
          onClick={() => definir({ contaId: null, periodo: null, de: null, ate: null })}
        >
          Limpar
        </Botao>
      ) : null}
    </div>
  )
}

export function rotularOperacao(operacao: ContaAnuncioResumo['operacao']): string {
  switch (operacao) {
    case 'VENDA':
      return 'Venda'
    case 'ALUGUEL':
      return 'Aluguel'
    default:
      return 'Outra'
  }
}

/**
 * O período resolvido, para os parâmetros da consulta.
 *
 * Só repassa `de`/`ate` quando o período é `PERSONALIZADO` — nos demais o servidor resolve
 * a faixa, porque "últimos 7 dias" é dia civil de São Paulo (RN-55) e o relógio do
 * navegador pode estar em qualquer fuso. Dois gestores veriam números diferentes para o
 * mesmo filtro se a conta fosse feita aqui.
 */
export function parametrosDoFiltro(filtros: Filtros): Record<string, string | undefined> {
  const periodo = filtros.periodo ?? '7D'

  return {
    contaId: filtros.contaId,
    periodo,
    ...(periodo === 'PERSONALIZADO' ? { de: filtros.de, ate: filtros.ate } : {}),
  }
}

function temFiltroAtivo(filtros: Filtros): boolean {
  return (
    filtros.contaId !== undefined ||
    (filtros.periodo !== undefined && filtros.periodo !== '7D')
  )
}
